'use strict';

/**
 * Inits credit card functionality on Storefront
 * @returns {void}
 */
function initCreditCardFunctionality() {
    const creditCardOnAccountEnabled = Boolean(document.querySelector('.js-braintree-add-credit-card-form'));
    const creditCardEnabledOnCheckout = Boolean(document.querySelector('.js-braintree-credit-card-content'));

    let creditCardBusinessLogic;

    if (creditCardOnAccountEnabled || creditCardEnabledOnCheckout) {
        creditCardBusinessLogic = require('./creditcard/creditCard');
        creditCardBusinessLogic.init();
    }

    if (creditCardOnAccountEnabled) {
        creditCardBusinessLogic.initAccount();
    }

    if (creditCardEnabledOnCheckout) {
        creditCardBusinessLogic.initCheckout();
    }
}

/**
 * Inits PayPal functionality on Storefront
 * @param {Promise} clientTokenPromise promise with Braintree Client Instance
 * @returns {void}
 */
async function initPayPalFunctionality(clientTokenPromise) {
    const payPalBusinessLogic = require('./paypal/payPal');

    const payPalEnabledOnCheckout = Boolean(document.querySelector('.js-braintree-paypal-content'));
    const payPalEnabledOnProduct = Boolean(document.querySelector('.prices-add-to-cart-actions .js-braintree-paypal-cart-button'));
    const payPalEnabledOnPvp = Boolean(document.querySelector('#quickViewModal .js-braintree-paypal-cart-button'));
    const payPalEnabledOnCart = Boolean(document.querySelector('.cart-page .js-braintree-paypal-cart-button'));
    const payPalEnabledOnAccountPage = Boolean(document.querySelector('.js-paypal-accounts'));
    const payPalEnabledOnLoginPage = Boolean(document.querySelector('.login-form-nav'));
    const cwppEnabledOnPage = Boolean(document.getElementById('js-cwpp-button'));
    const payPalBannerEnabled = Boolean(document.querySelector('.js-pp-banner'));

    payPalBusinessLogic.init(clientTokenPromise);

    const availablePages = [
        payPalEnabledOnProduct, payPalEnabledOnCart,
        payPalEnabledOnCheckout, payPalEnabledOnLoginPage,
        payPalEnabledOnAccountPage, cwppEnabledOnPage,
        payPalEnabledOnPvp
    ];

    if (availablePages.includes(true)) {
        const PayPalScriptLoading = require('./paypal/models/payPalScriptLoading');
        const cwpp = require('./paypal/components/connectWithPayPal.js');

        const payPalScriptLoading = new PayPalScriptLoading();

        if (cwpp.isContainerExist()) {
            await payPalScriptLoading.loadPayPalSDK();

            payPalScriptLoading.loadConnectWithPayPal(cwpp.init);
        }
    }

    if (payPalBannerEnabled && !(payPalEnabledOnProduct || payPalEnabledOnCart)) {
        const PayPalScriptLoading = require('./paypal/models/payPalScriptLoading');
        const payPalScriptLoading = new PayPalScriptLoading();

        payPalScriptLoading.loadPayPalCreditMessages();
    }

    if (payPalEnabledOnCheckout) {
        payPalBusinessLogic.initCheckout();
    }

    if (payPalEnabledOnProduct) {
        payPalBusinessLogic.initProduct();
    }

    if (payPalEnabledOnPvp) {
        payPalBusinessLogic.initQuickView();
    }

    // Validation on enabled/disabled PayPal button for MiniCart located isinside MiniCart component
    payPalBusinessLogic.initMiniCart();

    if (payPalEnabledOnCart) {
        payPalBusinessLogic.initCart();
    }

    if (payPalEnabledOnAccountPage) {
        payPalBusinessLogic.initAccount();
    }
}

/**
 * Inits GooglePay functionality on Storefront
 * @returns {void}
 */
function initGooglePayFunctionality() {
    const googlePayBusinnessLogic = require('./googlepay/googlePay');

    const googlePayEnabledOnCheckout = Boolean(document.querySelector('.js-braintree-billing-googlepay-button'));
    const googlePayEnabledOnCart = Boolean(document.querySelector('.js-braintree-cart-googlepay-button'));

    googlePayBusinnessLogic.init();

    if (googlePayEnabledOnCheckout) {
        googlePayBusinnessLogic.initCheckout();
    }

    if (googlePayEnabledOnCart) {
        googlePayBusinnessLogic.initCart();
    }
}

/**
 * Inits ApplePay functionality on Storefront
 * @returns {void}
 */
function initApplePayFunctionality() {
    const applePayBusinessLogic = require('./applepay/applePay');

    const applePayEnabledOnCheckout = Boolean(document.querySelector('.js-braintree-applepay-content'));
    const applePayEnabledOnCart = Boolean(document.querySelector('.js-braintree-applepay-button'));

    applePayBusinessLogic.init();

    if (applePayEnabledOnCart || applePayEnabledOnCheckout) {
        applePayBusinessLogic.initApplePay();
    }
}

/**
 * Inits Venmo functionality on Storefront
 * @returns {undefined} - Either removes Venmo block from the DOM or inits Venmo and returns undefined
 */
async function initVenmoFunctionality() {
    const isVenmoEligible = window.paypalVenmo
        && window.paypalVenmo.Buttons({ fundingSource: window.paypalVenmo.FUNDING.VENMO }).isEligible();

    if (!isVenmoEligible) {
        const venmoHelper = require('./venmo/helpers/venmoHelper.js');

        venmoHelper.removeVenmoBlock();

        return undefined;
    }

    const venmoExpressCheckout = require('./venmo/components/venmoExpressCheckout');

    venmoExpressCheckout.initVenmoOnMiniCartFunctionality();

    const isVenmoEnabledOnAccountPage = Boolean(document.querySelector('.js-venmo-card'));
    const isVenmoEnabledOnBillingPage = Boolean(document.querySelector('.js-braintree-venmo-content'));
    const isVenmoEnabledOnExpressCheckout = Boolean(document.querySelector('.js-venmo-express-checkout'));

    if (!(isVenmoEnabledOnAccountPage || isVenmoEnabledOnBillingPage || isVenmoEnabledOnExpressCheckout)) {
        return undefined;
    }

    const venmoBusinessLogic = require('./venmo/venmo');

    if (isVenmoEnabledOnAccountPage) {
        venmoBusinessLogic.initAccount();
    } else if (isVenmoEnabledOnExpressCheckout) {
        await venmoBusinessLogic.initExpressCheckout();
    } else if (isVenmoEnabledOnBillingPage) {
        await venmoBusinessLogic.initBillingCheckout();
    }

    return undefined;
}

/**
 * Inits SRC functionality on Storefront
 * @returns {void}
 */
function initSRCFunctionality() {
    const srcBusinessLogic = require('./src/src');

    const isSrcEnabledOnCartPage = Boolean(document.querySelector('.js-braintree-cart-src-button'));
    const isSrcEnabledOnBillingPage = Boolean(document.querySelector('.js-braintree-billing-src-button'));

    srcBusinessLogic.init();

    if (isSrcEnabledOnCartPage) {
        srcBusinessLogic.initCartCheckout();
    }

    if (isSrcEnabledOnBillingPage) {
        srcBusinessLogic.initBillingCheckout();
    }
}

/**
 * Inits LPM functionality on Storefront
 * @returns {void}
 */
function initLPMFunctionality() {
    const isLpmEnabledOnBillingPage = Boolean(document.querySelector('.js-braintree-lpm-content'));

    if (isLpmEnabledOnBillingPage) {
        const lpmHelper = require('./local/helpers/lpmHelper');
        const LpmsBuilder = require('./local/models/lpmsBuilder');
        const lpmsBuilderInstance = new LpmsBuilder();

        lpmsBuilderInstance.init();

        lpmHelper.handleNonInstantDisabledAttr();
    }

    // LPM fallback process
    if (document.querySelector('.js-lpm-fallback')) {
        const lpmFallback = require('./local/lpmFallBack');

        lpmFallback.process();
    }

    // Listener to show message from LPM fallback template
    window.addEventListener('message', (event) => {
        if (event.origin !== window.location.origin) {
            return;
        }

        if (typeof event.data === 'string' && event.data.includes('lpm-fallback-error')) {
            const AlertHandler = require('./local/alerthandler/lpmAlertHandlerModel.js');
            const alertHandler = new AlertHandler();

            const message = event.data.split(':')[1];

            alertHandler.showError(message);
        }
    });
}

/**
 * Inits APMA functionality on Storefront
 * @returns {void}
 */
function initAPMAFunctionality() {
    const apmaBusinessLogic = require('./apma');
    const isApmaEnebled = Boolean(document.getElementById('js-apma-button-paypal'));

    if (isApmaEnebled) {
        apmaBusinessLogic.init();
    }
}

/**
 * Inits Fastlane functionality on Storefront
 * @returns {void}
 */
async function initFastlaneFunctionality() {
    const isFastlaneEnabled = Boolean(document.querySelector('.js-braintree-fastlane-content'));

    if (isFastlaneEnabled) {
        const Fastlane = require('./fastlane/fastlane');

        await new Fastlane().init();
    }
}

/**
 * Handle payment`s errors on checkout page
 * @returns {void}
 */
function handlePaymentMethodErrorCheckout() {
    const helper = require('./helper');

    $(document).on('ajaxSuccess', function(event, jqXHR, ajaxSettings, response) {
        if (response.error && ['CheckoutServices-SubmitPayment', 'CheckoutServices-PlaceOrder'].includes(response.action)
            && !response.nonInstantLpmFlow) {
            fetch(helper.getUrlWithCsrfToken(`${window.braintreeUrls.deleteSessionPayments}`))
                .catch(() => {
                    window.location.reload();
                });

            helper.removeActiveSessionPayment(true);
        }
    });
}

module.exports = async function() {
    const { handleAdditionalAutotestCheckboxAttributes } = require('./helper');
    // Used only for Paypal. In future "client token" generation approach will be refactored
    const paymentMethodGeneral = require('./paymentMethodGeneral');

    // General global variables
    const isCheckoutPage = Boolean(document.getElementById('checkout-main'));
    const isAccountPage = Boolean(document.querySelector('.js-account-dashboard'));

    /* ------------------------General Account functionality------------------------ */
    if (isAccountPage) {
        const btAccountBusinessLogic = require('./braintreeAccount');
        // Common Credit Card and SRC Account functionality

        btAccountBusinessLogic.initAccount();
    }

    /* ------------------------General Checkout functionality----------------------- */
    if (isCheckoutPage) {
        paymentMethodGeneral.fillPaymentSummaryContainer();
        paymentMethodGeneral.initPaymentMethodTabClickBehavior();
        paymentMethodGeneral.initChangeShippingBehavior();
        paymentMethodGeneral.initBillingSelectorChangeFunctionality();
        paymentMethodGeneral.initAmountObserver();
        handlePaymentMethodErrorCheckout();
        handleAdditionalAutotestCheckboxAttributes();

        const lastAmount = parseFloat(window.sessionStorage.getItem('amount-used'));
        const isSessionPaymentsEnabled = window.braintreePreferences.isSessionPaymentsEnabled;

        if (!window.braintreePreferences.isFastlaneEnabled && [
            window.braintreeConstants.THREE_DS_FLOW_AUTO,
            window.braintreeConstants.THREE_DS_FLOW_ALWAYS
        ].includes(window.braintreePreferences.threeDSecureFlow)
            && lastAmount && isSessionPaymentsEnabled) {
            paymentMethodGeneral.handleActiveCCSessionPayment(lastAmount);
        }

        /* --------------------------Fastlane--------------------------- */
        if (window.braintreePreferences.isFastlaneEnabled) {
            await initFastlaneFunctionality();
        }
    }

    /* ------------------------Credit Card------------------------ */

    initCreditCardFunctionality();

    /* --------------------------PayPal-------------------------- */

    $('body').on('quickview:ready', async() => {
        await initPayPalFunctionality(paymentMethodGeneral.createGeneralClientInstancePromise());
        await initVenmoFunctionality();
    });

    await initPayPalFunctionality(paymentMethodGeneral.createGeneralClientInstancePromise());

    /* --------------------------GooglePay------------------------- */

    initGooglePayFunctionality();

    /* --------------------------ApplePay------------------------- */

    initApplePayFunctionality();

    /* --------------------------Venmo---------------------------- */

    await initVenmoFunctionality();

    /* --------------------------SRC------------------------------ */

    initSRCFunctionality();

    /* --------------------------LPM-------------------------- */

    initLPMFunctionality();

    /* --------------------------APMA-------------------------- */

    initAPMAFunctionality();

    /* --------------Clearing stored billing address id-------------- */

    if ([window?.braintreeUrls?.loginUrl, window?.braintreeUrls?.orderConfirmUrl].includes(window.location.pathname)) {
        const paymentMethodGeneralHelper = require('./paymentMethodGeneralHelper.js');

        paymentMethodGeneralHelper.removeStoredBillingAddressIds();
    }
};
